import { useCallback, useEffect, useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FlightsFilterForm } from 'legacyfrontend/components/filters';
import { useTranslation } from 'react-i18next';
import { isClient } from 'legacyfrontend/helpers/ssr-helper';

import PackagesApi from 'legacyfrontend/api/paquetes';
import { DATE_KEYS, ROUTE_KEYS } from 'legacyfrontend/constants';
import { useGlobal } from 'legacyfrontend/contexts/GlobalContext';
import { formatWithMomentFormat } from 'legacyfrontend/utils/dateUtils';

import { getCacheStatus } from 'legacyfrontend/helpers/getCacheStatus';
import ErrorSnackbar from '../../ErrorSnackbar';

const { BUSCAR_X_VUELOS } = ROUTE_KEYS;

const parseDateFromURL = (dateString) => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const initialFlightSetter = (params) => {
  const passengersParam = params?.get('passengers');
  const parsedPassengers = passengersParam ? passengersParam.split(',').map(Number) : [30];

  const result = {
    origin:
      params?.get('originId') && params?.get('originName')
        ? {
          id:
            params.get('originIdn') && !isNaN(Number(params.get('originIdn')))
              ? Number(params.get('originIdn'))
              : null,
          cityCode: params.get('originId'),
          cityName: params.get('originName').trim(),
        }
        : null,
    destination:
      params?.get('destinationId') && params?.get('destinationName')
        ? {
          id:
            params.get('destinationIdn') && !isNaN(Number(params.get('destinationIdn')))
              ? Number(params.get('destinationIdn'))
              : null,
          cityCode: params.get('destinationId'),
          cityName: params.get('destinationName').trim(),
        }
        : null,
    passengers: parsedPassengers,
    departureDate: parseDateFromURL(params?.get('departureDate')),
    returnDate: parseDateFromURL(params?.get('returnDate')),
    direct: false,
    baggage: false,
    cabin: 'Y',
    currency: 'USD',
    isOnlyOneWay: params?.get('isOnlyOneWay') === 'true',
  };

  return result;
};

const FlightsSearch = forwardRef(
  (
    {
      isOnTargetPath,
      background,
      text,
      onCloseMobileSearch,
      onSearchStart,
      onSearchComplete,
      isMobile,
      isLoading = false,
    },
    ref
  ) => {
    const { t } = useTranslation('layout');
    const history = useHistory();
    const { pathname, search } = useLocation();
    const { registerOnSearch } = useGlobal();

    const [isUpdatingFlightInfo, setIsUpdatingFlightInfo] = useState(false);

    const [errorMessage, setErrorMessage] = useState('');
    const [openToast, setOpenToast] = useState(false);

    const [isMobileSearchActive, setIsMobileSearchActive] = useState(false);
    const [hasAutoSearched, setHasAutoSearched] = useState(false);
    const [searchTimestamp, setSearchTimestamp] = useState(null);

    const initialFlightFormInput = useMemo(
      () =>
        initialFlightSetter(
          !!search && pathname === BUSCAR_X_VUELOS ? new URLSearchParams(search) : undefined
        ),
      [search, pathname]
    );

    const getInitialState = () => {
      if (isClient) {
        try {
          const flightInfo = sessionStorage.getItem('flightInfo');
          if (flightInfo) {
            const parsed = JSON.parse(flightInfo);
            const restoredState = {
              origin: parsed.origin ? {
                id: parsed.origin.id,
                cityCode: parsed.origin.cityCode,
                cityName: parsed.origin.cityName
              } : null,
              destination: parsed.destination ? {
                id: parsed.destination.id,
                cityCode: parsed.destination.cityCode,
                cityName: parsed.destination.cityName
              } : null,
              passengers: parsed.passengers || [30],
              departureDate: parsed.departureDate ? new Date(parsed.departureDate) : null,
              returnDate: parsed.returnDate ? new Date(parsed.returnDate) : null,
              isOnlyOneWay: parsed.isOnlyOneWay || false,
              direct: false,
              baggage: false,
              cabin: 'Y',
              currency: 'USD'
            };
            return restoredState;
          }
        } catch (error) {
        }
      }
      return initialFlightFormInput;
    };

    const [flightFormInput, setFlightFormInput] = useState(getInitialState);

    useEffect(() => {
      if (isClient) {
        try {
          const flightInfo = sessionStorage.getItem('flightInfo');
          if (flightInfo) {
            const parsed = JSON.parse(flightInfo);
            if (parsed.origin || parsed.destination || parsed.departureDate) {
              setFlightFormInput(prev => {
                const newState = {
                  ...prev,
                  origin: parsed.origin ? {
                    id: parsed.origin.id,
                    cityCode: parsed.origin.cityCode,
                    cityName: parsed.origin.cityName,
                    airportCode: parsed.origin.airportCode,
                    airportName: parsed.origin.airportName,
                    countryCode: parsed.origin.countryCode
                  } : prev.origin,
                  destination: parsed.destination ? {
                    id: parsed.destination.id,
                    cityCode: parsed.destination.cityCode,
                    cityName: parsed.destination.cityName,
                    airportCode: parsed.destination.airportCode,
                    airportName: parsed.destination.airportName,
                    countryCode: parsed.destination.countryCode
                  } : prev.destination,
                  passengers: parsed.passengers || prev.passengers,
                  departureDate: parsed.departureDate ? new Date(parsed.departureDate) : prev.departureDate,
                  returnDate: parsed.returnDate ? new Date(parsed.returnDate) : prev.returnDate,
                  isOnlyOneWay: parsed.isOnlyOneWay !== undefined ? parsed.isOnlyOneWay : prev.isOnlyOneWay
                };
                
                return newState;
              });
            }
          }
        } catch (error) {
        }
      }
    }, []);

    useEffect(() => {
      if (isClient && flightFormInput) {
        try {
          const existingFlightInfo = sessionStorage.getItem('flightInfo');
          let flightInfo = existingFlightInfo ? JSON.parse(existingFlightInfo) : {};
          
          if (flightFormInput.origin) {
            flightInfo.origin = {
              id: flightFormInput.origin.id,
              airportCode: flightFormInput.origin.airportCode || flightFormInput.origin.cityCode,
              airportName: flightFormInput.origin.airportName || flightFormInput.origin.cityName,
              cityCode: flightFormInput.origin.cityCode,
              cityName: flightFormInput.origin.cityName,
              countryCode: flightFormInput.origin.countryCode || flightInfo.origin?.countryCode
            };
            flightInfo.originId = flightFormInput.origin.cityCode;
            flightInfo.originName = flightFormInput.origin.cityName;
          }
          
          if (flightFormInput.destination) {
            flightInfo.destination = {
              id: flightFormInput.destination.id,
              airportCode: flightFormInput.destination.airportCode || flightFormInput.destination.cityCode,
              airportName: flightFormInput.destination.airportName || flightFormInput.destination.cityName,
              cityCode: flightFormInput.destination.cityCode,
              cityName: flightFormInput.destination.cityName,
              countryCode: flightFormInput.destination.countryCode || flightInfo.destination?.countryCode
            };
            flightInfo.destinationId = flightFormInput.destination.cityCode;
            flightInfo.destinationName = flightFormInput.destination.cityName;
          }
          
          if (flightFormInput.departureDate) {
            flightInfo.departureDate = flightFormInput.departureDate.toISOString().split('T')[0];
          }
          
          if (flightFormInput.returnDate) {
            flightInfo.returnDate = flightFormInput.returnDate.toISOString().split('T')[0];
          }
          
          if (flightFormInput.passengers) {
            flightInfo.passengers = flightFormInput.passengers;
          }
          
          if (flightFormInput.isOnlyOneWay !== undefined) {
            flightInfo.isOnlyOneWay = flightFormInput.isOnlyOneWay;
          }
          
          sessionStorage.setItem('flightInfo', JSON.stringify(flightInfo));
        } catch (error) {
        }
      }
    }, [flightFormInput, isMobile]);

    useEffect(() => {
      if (hasAutoSearched) return;

      const hasUrlParams = search && search.length > 1;

      if (hasUrlParams && pathname === BUSCAR_X_VUELOS) {
        const urlParams = new URLSearchParams(search);
        const originId = urlParams.get('originId');
        const originName = urlParams.get('originName');
        const originIdn = urlParams.get('originIdn');
        const destinationId = urlParams.get('destinationId');
        const destinationName = urlParams.get('destinationName');
        const destinationIdn = urlParams.get('destinationIdn');
        const departureDate = urlParams.get('departureDate');
        const returnDate = urlParams.get('returnDate');
        const passengers = urlParams.get('passengers');
        const isOnlyOneWay = urlParams.get('isOnlyOneWay');

        setFlightFormInput((prev) => {
          const needsUpdate = 
            (originId && originName && (!prev.origin || prev.origin.cityCode !== originId)) ||
            (destinationId && destinationName && (!prev.destination || prev.destination.cityCode !== destinationId)) ||
            (departureDate && (!prev.departureDate || new Date(prev.departureDate).toISOString().split('T')[0] !== departureDate)) ||
            (returnDate && (!prev.returnDate || new Date(prev.returnDate).toISOString().split('T')[0] !== returnDate)) ||
            (passengers && (!prev.passengers || prev.passengers.join(',') !== passengers)) ||
            (isOnlyOneWay && prev.isOnlyOneWay !== (isOnlyOneWay === 'true'));

          if (!needsUpdate) return prev;

          const newState = { ...prev };

          if (originId && originName) {
            newState.origin = {
              id: originIdn && !isNaN(Number(originIdn)) ? Number(originIdn) : null,
              cityCode: originId,
              cityName: originName.trim(),
            };
          }

          if (destinationId && destinationName) {
            newState.destination = {
              id: destinationIdn && !isNaN(Number(destinationIdn)) ? Number(destinationIdn) : null,
              cityCode: destinationId,
              cityName: destinationName.trim(),
            };
          }

          if (departureDate) {
            newState.departureDate = parseDateFromURL(departureDate);
          }

          if (returnDate) {
            newState.returnDate = parseDateFromURL(returnDate);
          }

          if (passengers) {
            newState.passengers = passengers.split(',').map(Number);
          }

          if (isOnlyOneWay) {
            newState.isOnlyOneWay = isOnlyOneWay === 'true';
          }

          return newState;
        });
      }
    }, [search, pathname, hasAutoSearched]);

    useImperativeHandle(ref, () => ({
      openFirstDialog: () => {
      },
      getFormInput: () => flightFormInput,
      triggerSearch: () => {
        handleSearchFlightsButton({ preventDefault: () => { } });
      },
      isFormValid: () => {
        return !!(
          flightFormInput.origin &&
          flightFormInput.destination &&
          flightFormInput.departureDate &&
          (flightFormInput.isOnlyOneWay || flightFormInput.returnDate) &&
          flightFormInput.passengers &&
          flightFormInput.passengers.length > 0
        );
      },
    }));


    const updateFlightInfo = useCallback(
      async (origin, destination, currentFlightFormInput) => {
        if (!origin || !destination || isUpdatingFlightInfo) return;

        try {
          setIsUpdatingFlightInfo(true);
          const [originData, destinationData] = await Promise.all([
            PackagesApi.getAirportById(origin.id),
            PackagesApi.getAirportById(destination.id),
          ]);

          if (!originData || !destinationData) {
            throw new Error('No se pudieron obtener los datos de los aeropuertos');
          }

          const isInternational = originData.countryCode !== destinationData.countryCode;
          const flightInfo = {
            originCountryCode: originData.countryCode,
            destinationCountryCode: destinationData.countryCode,
            origin: {
              id: originData.id,
              airportCode: originData.airportCode,
              airportName: originData.airportName,
              cityCode: originData.cityCode,
              cityName: originData.cityName,
              countryCode: originData.countryCode,
            },
            destination: {
              id: destinationData.id,
              airportCode: destinationData.airportCode,
              airportName: destinationData.airportName,
              cityCode: destinationData.cityCode,
              cityName: destinationData.cityName,
              countryCode: destinationData.countryCode,
            },
            departureDate: currentFlightFormInput.departureDate
              ? new Date(currentFlightFormInput.departureDate)
              : null,
            returnDate: currentFlightFormInput.returnDate
              ? new Date(currentFlightFormInput.returnDate)
              : null,
            passengers: currentFlightFormInput.passengers,
            isOnlyOneWay: currentFlightFormInput.isOnlyOneWay,
            isInternational,
          };

          sessionStorage.setItem('flightInfo', JSON.stringify(flightInfo));
          return flightInfo;
        } catch (error) {
          setErrorMessage('Error al actualizar la información del vuelo');
          setOpenToast(true);
        } finally {
          setIsUpdatingFlightInfo(false);
        }
      },
      [isUpdatingFlightInfo]
    );

    const handleFlightOrigin = useCallback(
      async (origin) => {
        if (!origin) return;

        try {
          setFlightFormInput((state) => {
            const newState = { ...state, origin };
            if (state.destination) {
              updateFlightInfo(origin, state.destination, newState).catch(() => {});
            }
            return newState;
          });
        } catch (error) {
        }
      },
      [updateFlightInfo]
    );

    const handleFlightDestination = useCallback(
      async (destination) => {
        if (!destination) return;

        try {
          setFlightFormInput((state) => {
            const newState = { ...state, destination };
            if (state.origin) {
              updateFlightInfo(state.origin, destination, newState).catch(() => {});
            }
            return newState;
          });
        } catch (error) {
        }
      },
      [updateFlightInfo]
    );

    const handleFlightPassenger = (passengers) => {
      setFlightFormInput((state) => ({ ...state, passengers }));
    };

    const handleFlightsDateRangeSelect = (departureDate, returnDate) => {
      setFlightFormInput({ ...flightFormInput, departureDate, returnDate });
    };

    const handleIsOnlyOneWay = (isOnlyOneWay) => {
      setFlightFormInput({ ...flightFormInput, isOnlyOneWay });
    };

    const handleOccupationChange = (totalTourists) => {
    };

    const [isDestinationEmpty, setIsDestinationEmpty] = useState(false);
    const [isOriginEmpty, setIsOriginEmpty] = useState(false);
    const [isDatesEmpty, setIsDatesEmpty] = useState(false);
    const [isTuristasEmpty, setIsTuristasEmpty] = useState(false);

    useEffect(() => {
      if (!flightFormInput) return;

      if (flightFormInput.origin) {
        setIsOriginEmpty(false);
      }
      if (flightFormInput.destination) {
        setIsDestinationEmpty(false);
      }
      if (flightFormInput.departureDate) {
        setIsDatesEmpty(false);
      }
      if (flightFormInput.passengers && flightFormInput.passengers.length > 0) {
        setIsTuristasEmpty(false);
      }
    }, [
      flightFormInput?.origin,
      flightFormInput?.destination,
      flightFormInput?.departureDate,
      flightFormInput?.passengers,
    ]);

    const showError = useCallback((message) => {
      setErrorMessage(message);
      setOpenToast(true);
    }, []);

    const handleCloseToast = useCallback(() => {
      setOpenToast(false);
    }, []);

    const getFlightsQueryString = useCallback(() => {
      const { origin, destination, departureDate, returnDate, passengers, isOnlyOneWay } =
        flightFormInput;

      const flightParams = new URLSearchParams();

      flightParams.set(
        'departureDate',
        formatWithMomentFormat(departureDate, DATE_KEYS.FORMAT.ISO)
      );

      if (returnDate)
        flightParams.set('returnDate', formatWithMomentFormat(returnDate, DATE_KEYS.FORMAT.ISO));

      if (origin) {
        flightParams.set('originId', origin.cityCode);
        flightParams.set('originName', origin.cityName);
        if (origin.id && !isNaN(origin.id)) {
          flightParams.set('originIdn', origin.id);
        }
      }

      if (destination) {
        flightParams.set('destinationId', destination.cityCode);
        flightParams.set('destinationName', destination.cityName);
        if (destination.id && !isNaN(destination.id)) {
          flightParams.set('destinationIdn', destination.id);
        }
      }

      if (passengers && Array.isArray(passengers)) {
        flightParams.set('passengers', passengers.join(','));
      } else if (passengers) {
        flightParams.set('passengers', '30');
      }

      if (isOnlyOneWay) flightParams.set('isOnlyOneWay', true);

      flightParams.set('cache', getCacheStatus());
      flightParams.set('_refresh', Date.now());

      return `?${flightParams.toString()}`;
    }, [flightFormInput]);

    const linkToFLights = useMemo(
      () =>
        flightFormInput?.destination && flightFormInput?.origin
          ? {
            pathname: ROUTE_KEYS.BUSCAR_X_VUELOS,
          }
          : '#',
      [flightFormInput]
    );

    const handleMobileSearchComplete = () => {
      setIsMobileSearchActive(false);
      if (onCloseMobileSearch) {
        onCloseMobileSearch();
      }
      if (onSearchComplete) {
        onSearchComplete();
      }
    };

    const clearSavedState = () => {
      if (isClient) {
        try {
        } catch (error) {
        }
      }
    };

    const handleSearchFlightsButton = useCallback(
      async (e) => {
        e.preventDefault();

        if (
          !flightFormInput?.origin ||
          !flightFormInput.origin.cityCode ||
          !flightFormInput.origin.cityName
        ) {
          showError('Por favor seleccione un origen válido');
          return;
        }
        if (
          !flightFormInput?.destination ||
          !flightFormInput.destination.cityCode ||
          !flightFormInput.destination.cityName
        ) {
          showError('Por favor seleccione un destino válido');
          return;
        }
        if (
          !flightFormInput?.departureDate ||
          isNaN(new Date(flightFormInput.departureDate).getTime())
        ) {
          showError('Por favor seleccione una fecha de salida válida');
          return;
        }
        if (
          !flightFormInput?.isOnlyOneWay &&
          (!flightFormInput?.returnDate || isNaN(new Date(flightFormInput.returnDate).getTime()))
        ) {
          showError('Por favor seleccione una fecha de regreso válida');
          return;
        }
        if (
          !flightFormInput?.passengers ||
          !Array.isArray(flightFormInput.passengers) ||
          flightFormInput.passengers.length === 0
        ) {
          setFlightFormInput((prev) => ({ ...prev, passengers: [30] }));
        }

        try {
          if (onSearchStart) {
            onSearchStart();
          }

          await updateFlightInfo(
            flightFormInput.origin,
            flightFormInput.destination,
            flightFormInput
          );
          setSearchTimestamp(Date.now());

          if (isMobile) {
            setTimeout(() => {
              history.push(linkToFLights.pathname + getFlightsQueryString());
              registerOnSearch();
              clearSavedState();
              handleMobileSearchComplete();
            }, 500);
          } else {
            history.push(linkToFLights.pathname + getFlightsQueryString());
            registerOnSearch();
            clearSavedState();
          }
        } catch (error) {
          showError(error?.message || 'Error al procesar la búsqueda');
          if (onSearchComplete) {
            onSearchComplete();
          }
        }
      },
      [
        linkToFLights,
        getFlightsQueryString,
        history,
        registerOnSearch,
        flightFormInput,
        showError,
        updateFlightInfo,
        isMobile,
        handleMobileSearchComplete,
      ]
    );

    const isSearchButtonDisabled = () => {
      return (
        !flightFormInput.origin || !flightFormInput.destination || !flightFormInput.departureDate
      );
    };

    useEffect(() => {
      if (hasAutoSearched || isMobile) return;

      if (!flightFormInput) return;

      const hasAllRequiredValues =
        flightFormInput.origin &&
        flightFormInput.destination &&
        flightFormInput.departureDate &&
        flightFormInput.passengers &&
        flightFormInput.passengers.length > 0;

      const hasUrlParams = search && search.length > 1;

      if (hasAllRequiredValues && hasUrlParams && pathname === BUSCAR_X_VUELOS) {
        setHasAutoSearched(true);

        try {
          handleSearchFlightsButton({ preventDefault: () => { } });
        } catch (error) {
        }
      }
    }, [flightFormInput, search, pathname, hasAutoSearched, isMobile]);

    if (isMobile) {
      return (
        <>
          <div className='package-search__form__container'>
            <div className='package-search__form__fields'>
              <FlightsFilterForm
                handleOriginSelect={handleFlightOrigin}
                handleDestinationSelect={handleFlightDestination}
                handleDateRangeSelect={handleFlightsDateRangeSelect}
                handleDepartureDateChange={handleFlightsDateRangeSelect}
                handleReturnDateChange={handleFlightsDateRangeSelect}
                handleFlightPassenger={handleFlightPassenger}
                handleIsOnlyOneWay={handleIsOnlyOneWay}
                handleOccupationChange={handleOccupationChange}
                formInput={flightFormInput}
                isOriginEmpty={isOriginEmpty}
                isDestinationEmpty={isDestinationEmpty}
                setIsOriginEmpty={setIsOriginEmpty}
                setIsDestinationEmpty={setIsDestinationEmpty}
                isDatesEmpty={false}
                isTuristasEmpty={false}
                setIsDatesEmpty={() => { }}
                onMobileSearchComplete={handleMobileSearchComplete}
                isMobileSearchActive={false}
              />
            </div>
          </div>

          <ErrorSnackbar open={openToast} message={errorMessage} onClose={handleCloseToast} />
        </>
      );
    }

    if (!isOnTargetPath) return <></>;

    return (
      <>
        <div className='package-search__form__container'>
          <div
            className='package-search__form__fields'
            style={{ display: 'flex', alignItems: 'center', gap: '16px' }}
          >
            <FlightsFilterForm
              handleOriginSelect={handleFlightOrigin}
              handleDestinationSelect={handleFlightDestination}
              handleDateRangeSelect={handleFlightsDateRangeSelect}
              handleDepartureDateChange={handleFlightsDateRangeSelect}
              handleReturnDateChange={handleFlightsDateRangeSelect}
              handleFlightPassenger={handleFlightPassenger}
              handleIsOnlyOneWay={handleIsOnlyOneWay}
              handleOccupationChange={handleOccupationChange}
              formInput={flightFormInput}
              isOriginEmpty={isOriginEmpty}
              isDestinationEmpty={isDestinationEmpty}
              setIsOriginEmpty={setIsOriginEmpty}
              setIsDestinationEmpty={setIsDestinationEmpty}
              isDatesEmpty={false}
              isTuristasEmpty={false}
              setIsDatesEmpty={() => { }}
              onMobileSearchComplete={handleMobileSearchComplete}
              isMobileSearchActive={isMobileSearchActive}
            />
            <div className='package-search__button-row'>
              <button
                onClick={handleSearchFlightsButton}
                disabled={isSearchButtonDisabled()}
                className='package-search__button big-package-search__button'
                style={{
                  backgroundColor: background,
                  color: text,
                  padding: '12px 16px',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                }}
              >
                <i className='fa fa-search heading__icon' />
                {t('common.search') || 'Buscar'}
              </button>
            </div>
          </div>
        </div>

        <ErrorSnackbar open={openToast} message={errorMessage} onClose={handleCloseToast} />
      </>
    );
  }
);

export default FlightsSearch;
